import {IStorefrontSuggestedBundle, STOREFRONT_SUGGESTION_SOURCE} from '../../models/Storefront';
import {ISuggestionReason} from './suggestedProductsLogic';

/**
 * Pure helpers behind the "Suggested Bundles" strip on a storefront's Bundles tab.
 *
 * The bundle twin of suggestedProductsLogic, kept apart from the components so the wording rules can be read
 * and exercised without mounting Fluent. There is no listing-body helper here: a bundle is listed through a
 * route that takes its id and nothing else.
 */

/** How many bundle suggestions the strip asks for. One row of cards, no more. */
export const BUNDLE_STRIP_SIZE = 4;

/** How many the See all panel asks for. The API caps at 12. */
export const BUNDLE_PANEL_SIZE = 12;

/**
 * Why this bundle is being suggested, as a short chip plus the sentence behind it.
 *
 * Two real answers: another storefront already carries it, or it is simply sitting in the catalog unlisted.
 * The first reuses the product wording verbatim, because the same fact is being stated about a different kind
 * of thing and two phrasings for it would read as two different things. An unrecognised source falls through
 * to a neutral "Suggested" rather than being read as one of the known ones, so a source this build has never
 * heard of is left unlabelled rather than mislabelled.
 */
export const bundleSuggestionReason = (bundle: IStorefrontSuggestedBundle): ISuggestionReason => {
    switch (bundle.source) {
        case STOREFRONT_SUGGESTION_SOURCE.ON_STOREFRONTS: {
            const count = bundle.storefrontCount;
            if (count === undefined || count === null || count <= 0) {
                return {
                    label: 'On Other Storefronts',
                    tooltip: 'Already listed on your other storefronts',
                };
            }
            return {
                label: `On ${count} Storefront${count === 1 ? '' : 's'}`,
                tooltip: count === 1
                    ? 'Already listed on one of your other storefronts'
                    : `Already listed on ${count} of your other storefronts`,
            };
        }
        case STOREFRONT_SUGGESTION_SOURCE.CATALOG:
            return {
                label: 'In Your Catalog',
                tooltip: 'An active bundle from your catalog, not yet on this store',
            };
        default:
            return {label: 'Suggested', tooltip: 'Suggested for this store'};
    }
};

/** The three honest availability states a suggested bundle can be in. */
export interface IBundleAvailability {
    kind: 'inStock' | 'outOfStock' | 'unpriced';
    label: string;
    /** The fuller sentence, shown on hover. Only the unpriced state has more to say than its label. */
    tooltip?: string;
}

/**
 * What to say about whether this bundle can be sold today.
 *
 * "Out of stock" and "could not be priced" are different problems with different fixes: the first waits on a
 * distributor, the second waits on someone sourcing a component the store's vendor filters cannot price. The
 * card used to collapse both into "Out of stock", which sent the operator looking at inventory when the real
 * blocker was a part number with no priced vendor. The unpriced reason wins over the stock dot because an
 * unpriceable bundle cannot be sold whatever the shelves say.
 */
export const bundleAvailability = (bundle: IStorefrontSuggestedBundle): IBundleAvailability => {
    if (bundle.unpricedReason) {
        // The reason is usually vendor sourcing but not always (an empty bundle reads "No products in bundle"),
        // so the label follows the reason rather than asserting a vendor problem that may not exist.
        const vendorProblem = bundle.unpricedReason.startsWith('No priced vendor');
        return {
            kind: 'unpriced',
            label: vendorProblem ? 'No priced vendor' : 'Not priceable',
            tooltip: bundle.unpricedReason,
        };
    }
    if (bundle.inStock) return {kind: 'inStock', label: 'In stock'};
    return {kind: 'outOfStock', label: 'Out of stock'};
};

/**
 * The muted second line of a bundle card: how many products are in it.
 *
 * A bundle reporting no products is a bundle mid-assembly, and "0 products" is a worse thing to print under a
 * name than nothing at all, so the line is dropped instead.
 */
export const bundleDetailLine = (bundle: IStorefrontSuggestedBundle): string | undefined => {
    const count = bundle.productCount;
    if (!count || count <= 0) return undefined;
    return `${count} product${count === 1 ? '' : 's'}`;
};
